/**
 * Boîte à outils — « film » d'une expérience : l'instant t avance de 0 à T en `screen` secondes
 * d'écran (requestAnimationFrame), bouton lecture / pause et curseur « Instant ». Un changement
 * de `resetKey` remet le film au début.
 */

import { useEffect, useRef, useState } from 'react';
import { Pause, Play } from 'lucide-react';
import { ParamSlider } from './controls';
import { frenchSpaces } from './format';

export function usePlayer(T: number, screen: number, resetKey: string) {
  const [t, setT] = useState(0);
  const [playing, setPlaying] = useState(false);
  useEffect(() => {
    setT(0);
    setPlaying(false);
  }, [resetKey]);
  const raf = useRef<number | null>(null);
  useEffect(() => {
    if (!playing) return;
    const rate = T / screen;
    let last = performance.now();
    const step = (now: number) => {
      const dt = ((now - last) / 1000) * rate;
      last = now;
      let stop = false;
      setT((x) => {
        const nx = Math.min(T, x + dt);
        if (nx >= T) stop = true;
        return nx;
      });
      if (stop) setPlaying(false);
      else raf.current = requestAnimationFrame(step);
    };
    raf.current = requestAnimationFrame(step);
    return () => {
      if (raf.current !== null) cancelAnimationFrame(raf.current);
    };
  }, [playing, T, screen]);
  return {
    t,
    setT: (v: number) => {
      setPlaying(false);
      setT(Math.min(Math.max(v, 0), T));
    },
    playing,
    toggle: () => {
      if (!playing && t >= T - 1e-12) setT(0);
      setPlaying(!playing);
    },
  };
}

export function PlayBar({
  player,
  T,
  format,
}: {
  player: ReturnType<typeof usePlayer>;
  T: number;
  format: (t: number) => string;
}) {
  const steps = 200;
  return (
    <div className="flex items-center gap-3">
      <button
        type="button"
        aria-label={player.playing ? 'Pause' : 'Lancer le film'}
        aria-pressed={player.playing}
        onClick={player.toggle}
        className="bg-primary-500 flex min-h-11 min-w-11 items-center justify-center rounded-xl text-white active:scale-[0.98]"
      >
        {player.playing ? (
          <Pause className="h-5 w-5" aria-hidden />
        ) : (
          <Play className="h-5 w-5" aria-hidden />
        )}
      </button>
      <div className="flex-1">
        <ParamSlider
          name="Instant t"
          ariaLabel="Instant"
          value={Math.round((player.t / T) * steps)}
          min={0}
          max={steps}
          step={1}
          onChange={(v) => player.setT((v / steps) * T)}
          format={() => frenchSpaces(format(player.t))}
        />
      </div>
    </div>
  );
}
